<template>
  <div class="relative" style="height: 120px; overflow: visible;">
    <!-- Линия нулевого уровня -->
    <div
        v-if="hasPnlData"
        class="absolute left-0 right-0 top-1/2 border-t"
        :class="[$theme.navBorderColor]"
    ></div>

    <!-- Плейсхолдер -->
    <div
        v-if="!hasPnlData"
        class="flex items-center justify-center h-full text-sm opacity-70"
        :class="[$theme.cardLabelTextColor]"
    >
      {{ $t('no_display_data') }}
    </div>

    <!-- Столбики -->
    <div v-else class="relative flex justify-around items-center h-full">
      <div
          v-for="(value, index) in pnlNumeric"
          :key="index"
          class="relative h-full flex flex-col items-center"
          @mouseenter="showTooltip($event, value, dateAt(index), index)"
          @mousemove="moveTooltip($event, value)"
          @mouseleave="hideTooltip"
      >
        <div
            :style="barStyle(value)"
            :class="[
            'transition-all duration-300 cursor-pointer shadow-lg',
            value > 0 ? 'rounded-t' : (value < 0 ? 'rounded-b' : 'rounded'),
            value > 0 ? $theme.bgSuccessPnlColor : (value < 0 ? $theme.bgDangerColor : $theme.navBorderColor),
            barWidthClass,
          ]"
        ></div>
      </div>
    </div>

    <!-- Tooltip переносим в body -->
    <teleport to="body">
      <div
          v-if="tooltip.visible"
          class="fixed text-xs px-2 py-1 rounded shadow-lg z-50 w-28 border text-center pointer-events-none"
          :class="[$theme.baseBg, $theme.baseTextColor, $theme.borderOrange]"
          :style="{
          left: tooltip.x + 'px',
          top: tooltip.y + 'px',
          transform: tooltip.transform
        }"
      >
        <div>{{ formatedDate(tooltip.date) }}</div>
        <div>PnL: {{ roundAmount(tooltip.pnl) }}</div>
      </div>
    </teleport>
  </div>
</template>

<script>
import { roundAmount } from '@/helpers/numberHelper'
import { formatedDate } from '@/helpers/dateHelper'

export default {
  name: 'PnlMiniChart',
  props: {
    chart_data: { type: Object, required: true },
    period: { type: String, default: '7days' },
  },
  data() {
    return {
      tooltip: {
        visible: false,
        index: null,
        pnl: null,
        date: null,
        x: 0,
        y: 0,
        transform: 'translate(-50%, -100%)', // вверх по умолчанию
      },
      roundAmount,
      formatedDate,
      TOOLTIP_W: 140,  // приблизительная ширина тултипа (px)
      GAP: 8,          // отступ от края экрана/бара
    }
  },
  computed: {
    pnlNumeric() {
      const arr = Array.isArray(this.chart_data?.pnl) ? this.chart_data.pnl : []
      return arr.map((v) => {
        const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'))
        return Number.isFinite(n) ? n : 0
      })
    },
    hasPnlData() {
      return this.pnlNumeric.some((v) => v !== 0)
    },
    maxPnl() {
      const max = Math.max(...this.pnlNumeric.map((val) => Math.abs(val)), 0)
      return max || 1
    },
    barWidthClass() {
      if (this.period === '7days') return 'w-9'
      if (this.period === '14days') return 'w-4'
      if (this.period === 'month') return 'w-2'
      return 'w-3'
    },
  },
  methods: {
    dateAt(i) {
      const dts = Array.isArray(this.chart_data?.dates) ? this.chart_data.dates : []
      return dts[i] ?? null
    },
    barStyle(value) {
      const hRaw = (Math.abs(value) / this.maxPnl) * 60
      const h = value !== 0 ? Math.max(hRaw, 2) : 2
      if (value > 0) return { position: 'absolute', bottom: '50%', height: `${h}px` }
      if (value < 0) return { position: 'absolute', top: '50%', height: `${h}px` }
      return { position: 'absolute', top: 'calc(50% - 1px)', height: '2px' }
    },

    // ===== Tooltip logic (через Teleport) =====
    showTooltip(e, pnl, date, index) {
      this.tooltip.visible = true
      this.tooltip.index = index
      this.tooltip.pnl = pnl
      this.tooltip.date = date
      this.placeTooltip(e, pnl, /*preferAbove*/ pnl >= 0)
    },
    moveTooltip(e, pnl) {
      if (!this.tooltip.visible) return
      this.placeTooltip(e, pnl, /*preferAbove*/ pnl >= 0)
    },
    hideTooltip() {
      this.tooltip.visible = false
    },
    placeTooltip(e, pnl, preferAbove = true) {
      const vw = window.innerWidth
      const vh = window.innerHeight
      const mouseX = e.clientX
      const mouseY = e.clientY

      // Горизонтальный клэмп
      const halfW = this.TOOLTIP_W / 2
      const minX = this.GAP + halfW
      const maxX = vw - this.GAP - halfW
      const clampedX = Math.max(minX, Math.min(maxX, mouseX))

      // Вертикальный флип: если сверху мало места — рисуем снизу
      const spaceTop = mouseY
      const spaceBottom = vh - mouseY
      const showAbove = preferAbove ? spaceTop >= 56 : false
      const offsetY = 12

      let top = mouseY - offsetY
      let transform = 'translate(-50%, -100%)' // вверх
      if (!showAbove && spaceBottom > 56) {
        top = mouseY + offsetY
        transform = 'translate(-50%, 0)'
      }

      this.tooltip.x = clampedX
      this.tooltip.y = top
      this.tooltip.transform = transform
    },
  },
}
</script>
